// Payouts → Ownership — who owns which axis, when, by name.
//
// Money resolves the owner AS OF THE PLAY-DAY and stamps it on the line
// forever: re-homing a cab or transferring a screen never rewrites who
// earned July. That is why this screen exists (analytics resolves the
// current owner at read; money cannot), why transfers are forward-only, and
// why absence is meaningful — an axis with no owner row is treated as
// house-owned and its share prints as an ordinary pDOOH line, never residual.
//
// BUT ABSENCE IS AMBIGUOUS, AND THE ENGINE CANNOT TELL. shareEngine.js:193 is
// `if (!owner) return 'house'`, commented "house-owned: ours either way" — an
// assumption, not a fact. A screen that is genuinely ours and a screen nobody
// registered are the same input to that line. So this screen now also lists
// what the register is SILENT about: every subject that has sent operator or
// owner money to house for want of a row. That turns house-by-omission into
// house-by-decision. It moves no money.
//
// Everything is picked by NAME — the cab node's name, the screen's field
// name, the tenant — because nobody should ever paste a uuid into a money
// screen. The POST goes through the thin proxy to billing-service, the one
// writer.

import React, { useMemo, useState } from 'react';
import { PageHead, Loading, ErrorState, Pill, Button } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import { BASE, shaped, inr } from './lib';
import Explainer from './Explainer';
import s from './Payouts.module.css';

const fmtTs = (t) => (t ? new Date(t).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null);

function TransferForm({ subjects, onDone }) {
  const [axis, setAxis] = useState('edge_node');
  const [subjectId, setSubjectId] = useState('');
  const [owner, setOwner] = useState('');
  const [result, setResult] = useState(null);

  const options = axis === 'edge_node'
    ? (subjects?.nodes || []).map((n) => ({ id: n.id, label: n.node_name || n.id.slice(0, 8) }))
    : (subjects?.devices || []).map((d) => ({ id: d.id, label: d.thing_name || d.id.slice(0, 8) }));

  const submit = async () => {
    setResult(null);
    try {
      const r = await api.post(`${BASE}/ownership/transfer`, {
        subject_type: axis,
        subject_id: subjectId,
        owner_tenant_id: owner || null,
      });
      setResult({
        ok: true,
        text: owner
          ? `Transferred, effective ${new Date(r.data.effective_at).toLocaleString('en-IN')}. Everything earned before that instant stays with the previous owner, permanently.`
          : 'Reverted to pDOOH (the house), effective now.',
      });
      setSubjectId('');
      onDone();
    } catch (e) {
      setResult({ ok: false, text: e?.response?.data?.details || e?.response?.data?.error || e.message });
    }
  };

  return (
    <div className={s.panel}>
      <div className={s.panelTitle}>Transfer ownership — effective now, never backdated</div>
      <div className={s.formRow} style={{ marginBottom: 0 }}>
        <div className={s.field}>
          <label>What</label>
          <select value={axis} onChange={(e) => { setAxis(e.target.value); setSubjectId(''); }}>
            <option value="edge_node">A premise (the cab node)</option>
            <option value="device">A screen (the device)</option>
          </select>
        </div>
        <div className={`${s.field} ${s.fieldWide}`}>
          <label>{axis === 'edge_node' ? 'Cab node' : 'Screen'}</label>
          <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
            <option value="">choose…</option>
            {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
        <div className={`${s.field} ${s.fieldWide}`}>
          <label>New owner</label>
          <select value={owner} onChange={(e) => setOwner(e.target.value)}>
            <option value="">pDOOH (the house)</option>
            {(subjects?.tenants || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <Button primary onClick={submit} disabled={!subjectId}>Transfer</Button>
      </div>
      {result && <div className={result.ok ? s.okBanner : s.badBanner}>{result.text}</div>}
    </div>
  );
}

// ── WHAT THE REGISTER DOES NOT SAY ──────────────────────────────────────────
//
// Every row here is a screen whose premise-operator or device-owner share went
// to pDOOH because no ownership row existed — not because anyone decided it
// should.
//
// DELIBERATELY NOT A TEST-DEVICE BADGE. The founder ruled on 24 Sep that a lab
// screen stays in the monetisation chain as a true device, so "is it ours on
// purpose" is not the question and is_test_device is not the answer. The badge
// reports the only thing that decides money: whether any axis claims this
// screen, and whether the claimant is someone we invoice.
//
// The fix for a row is a TRANSFER, which is forward-only by design. It will NOT
// re-home money already earned — lines stamp the owner as of the play-day and
// are never rewritten. So a row here is a decision about the future and a
// disclosure about the past, and the caption says so rather than implying a
// button can undo it.
function UnregisteredEarners({ rows }) {
  if (!rows.length) return null;
  const total = rows.reduce((a, r) => a + Number(r.total_paise || 0), 0);
  return (
    <div className={s.panel}>
      <div className={s.panelTitle}>
        Earning, but not in the register — {rows.length} {rows.length === 1 ? 'screen' : 'screens'}, {inr(total)} to house
      </div>
      <p className={s.panelSub}>
        These screens earned, and their operator and owner share became pDOOH’s because no ownership row covered
        them on the play-day. That may be right — a bench unit or a house screen belongs to us either way — but
        nothing here recorded it as a choice. A transfer fixes the future; it never re-homes money already earned.
      </p>
      {/* THE SECOND AXIS, and the more consequential half. Ownership is recorded
          twice: the fold's FLEET side resolves a premise through
          COALESCE(ownership_history, edge_nodes_devices.tenant_id), while its
          MONEY side and the invoice issuer read ownership_history alone. A
          premise named only by the fleet axis has its days judged and its
          supply billed to advertisers, and is invoiced to nobody — silently,
          because no screen ever compared the two. Measured 24 Sep: 7 of 25
          in_cab premises, ₹41,243.31 of billed supply since the console epoch.
          Every case is one-sided silence; not one premise names two DIFFERENT
          tenants. So naming the silent side is the whole of the fix. */}
      {rows.some((r) => r.fleet_axis_named && !r.fleet_tenant_is_media_owner) && (
        <p className={s.panelSub}>
          <b>Some of these are named on the other axis.</b> A screen whose cab is registered to a tenant under
          <i> fleet</i> ownership, but which has no entry in this register, is folded into that tenant’s day
          verdicts and into nobody’s money — so its supply is billed to advertisers and invoiced to no one. It is
          not lost and it is not wrong; it is simply unclaimed, and it stays that way until someone says whose it is.
        </p>
      )}
      {rows.map((r) => (
        <div key={`${r.device_id}|${r.edge_node_id}`} className={s.subjectRow}>
          <span className={s.subjectName}>{r.thing_name || String(r.device_id).slice(0, 8)}</span>
          <span className={s.subjectAxis}>
            {r.device_unregistered && r.node_unregistered ? 'screen + premise'
              : r.device_unregistered ? 'screen' : 'premise'}
          </span>
          <span className={s.ownerChip}>
            <b>{inr(r.total_paise)}</b> to house — {inr(r.premise_operator_paise)} premise operator,{' '}
            {inr(r.device_owner_paise)} device owner
          </span>
          {/* NOT a test-device pill. Founder, 24 Sep: "i dont want the is test
              device thingi… its fine if those devices are accruing advertising
              money… they remain part of the monetization chain as a true device
              even though lab/test". So a lab screen is a screen, and the only
              question worth a badge is the one that decides money: has anyone
              claimed it. */}
          <Pill tone={r.fleet_axis_named ? (r.fleet_tenant_is_media_owner ? 'iris' : 'pend') : 'bad'}>
            {r.fleet_axis_named
              ? (r.fleet_tenant_is_media_owner ? 'claimable' : 'unclaimed')
              : 'house by default'}
          </Pill>
          <span className={s.hint}>
            {r.first_day === r.last_day ? r.first_day : `${r.first_day} → ${r.last_day}`}
            {r.lifecycle_status ? ` · ${r.lifecycle_status}` : ''}
            {r.fleet_axis_named && (
              <>
                {' · '}fleet axis says <b>{r.fleet_axis_tenant}</b>
                {r.fleet_tenant_is_media_owner
                  ? ' — a media owner, so registering this screen would invoice them'
                  : ' — which the money side never sees, so this supply is invoiced to nobody'}
              </>
            )}
          </span>
        </div>
      ))}
      <p className={s.hint} style={{ marginTop: 'var(--s3)' }}>
        Register the ones that belong to a fleet, and leave the ones that are genuinely ours — either way the
        answer stops being an accident. Nothing on this panel moves a rupee.
      </p>
    </div>
  );
}

export default function Ownership() {
  const { data, error, loading, reload } = useApi(
    () => api.get(`${BASE}/ownership`).then((r) => shaped(r.data, 'ownership')), []);
  const subjects = useApi(() => api.get(`${BASE}/subjects`).then((r) => shaped(r.data, 'tenants')), []);

  const nameOf = useMemo(() => {
    const m = new Map();
    for (const n of subjects.data?.nodes || []) m.set(n.id, n.node_name || n.id.slice(0, 8));
    for (const d of subjects.data?.devices || []) m.set(d.id, d.thing_name || d.id.slice(0, 8));
    return m;
  }, [subjects.data]);

  const head = (
    <PageHead
      title="Ownership"
      sub="Who owns each cab and each screen, as dated history — money stamps the owner as of the play-day, so a transfer never rewrites who earned the past."
      actions={<Button onClick={reload}>Refresh</Button>}
    />
  );

  if (loading || subjects.loading) return <div className={s.wrap}>{head}<Loading what="ownership" /></div>;
  if (error) return <div className={s.wrap}>{head}<ErrorState error={error} onRetry={reload} /></div>;

  const rows = data?.ownership || [];
  const grouped = new Map();
  for (const r of rows) {
    const key = `${r.subject_type}|${r.subject_id}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(r);
  }

  return (
    <div className={s.wrap}>
      {head}
      <TransferForm subjects={subjects.data} onDone={reload} />

      <div className={s.panel}>
        <div className={s.panelTitle}>The register — {grouped.size} subjects under external ownership</div>
        {[...grouped.entries()].map(([key, hist]) => {
          const [axis, id] = key.split('|');
          const current = hist.find((h) => h.valid_to == null);
          return (
            <div key={key} className={s.subjectRow}>
              <span className={s.subjectName}>{nameOf.get(id) || id.slice(0, 8)}</span>
              <span className={s.subjectAxis}>{axis === 'edge_node' ? 'premise · cab' : 'screen'}</span>
              {current
                ? <span className={s.ownerChip}>owned by <b>{current.owner_name}</b> since {fmtTs(current.valid_from)}</span>
                : <Pill tone="pend">house</Pill>}
              {hist.length > 1 && (
                <span className={s.hint}>
                  {hist.length - 1} earlier {hist.length - 1 === 1 ? 'interval' : 'intervals'}:{' '}
                  {hist.filter((h) => h.valid_to != null)
                    .map((h) => `${h.owner_name} ${fmtTs(h.valid_from)}–${fmtTs(h.valid_to)}`).join(' · ')}
                </span>
              )}
            </div>
          );
        })}
        <p className={s.hint} style={{ marginTop: 'var(--s3)' }}>
          Everything not listed is treated as house-owned: its supply share prints as an ordinary pDOOH line,
          never residual. Where that has actually happened, it is listed below by name.
        </p>
      </div>

      <UnregisteredEarners rows={data?.unregistered || []} />

      <Explainer terms={['ownership', 'premise', 'deviceOwner', 'house']} />
    </div>
  );
}
